import React from "react";

const ProcedureCard = ({
  procedureName = "Unknown Procedure",
  status = "unknown",
  performedDate = "",
  performedPeriod = null,
  performers = [],
  bodySites = [],
  outcome = "",
  reasonCodes = [],
  notes = [],
  isDarkTheme = true,
  icd10Codes = [],
  procedureType = "",
  procedureCode = "",
}) => {
  const useResponsiveStyles = () => {
    return {
      isMobile: window.innerWidth < 600,
    };
  };

  const { isMobile } = useResponsiveStyles();

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Get performed date display
  const getPerformedDateDisplay = () => {
    if (performedDate) {
      return formatDate(performedDate);
    }
    if (performedPeriod?.start && performedPeriod?.end) {
      return `${formatDate(performedPeriod.start)} - ${formatDate(
        performedPeriod.end
      )}`;
    }
    if (performedPeriod?.start) {
      return formatDate(performedPeriod.start);
    }
    return "Date not available";
  };

  const styles = createStyles(isMobile, isDarkTheme);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h5 style={styles.title}>{procedureName}</h5>
      </div>

      {/* Details */}
      <div style={styles.detailsContainer}>
        {procedureType && (
          <div style={styles.detailRow}>
            <span style={styles.label}>Procedure Type:</span>
            <span style={styles.value}>{procedureType}</span>
          </div>
        )}

        {procedureCode && (
          <div style={styles.detailRow}>
            <span style={styles.label}>Procedure Code:</span>
            <span style={styles.value}>{procedureCode}</span>
          </div>
        )}

        <div style={styles.detailRow}>
          <span style={styles.label}>
            ICD-10 Code{icd10Codes.length !== 1 ? "s" : ""}:
          </span>
          {icd10Codes && icd10Codes.length > 0 ? (
            <div style={styles.codesWrapper}>
              {icd10Codes.map((code, idx) => (
                <span key={idx} style={styles.codeBadge}>
                  {code}
                </span>
              ))}
            </div>
          ) : (
            <span style={styles.placeholder}>No ICD-10 codes available</span>
          )}
        </div>

        {notes && notes.length > 0 && (
          <div style={styles.notesSection}>
            <div style={styles.notesHeader}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={styles.notesIcon}
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              <span style={styles.notesLabel}>Notes</span>
            </div>
            <div style={styles.notesContent}>
              {notes.map((note, idx) => (
                <div key={idx} style={styles.noteItem(idx, notes.length)}>
                  {note}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Extracted styles object
const createStyles = (isMobile, isDarkTheme) => ({
  container: {
    backgroundColor: isDarkTheme ? "#433F81" : "#dfe0f7",
    borderRadius: "12px",
    padding: isMobile ? "12px" : "16px",
    color: isDarkTheme ? "white" : "#333",
    width: "100%",
    maxWidth: "1000px",
    boxShadow: isDarkTheme
      ? "0 8px 24px rgba(0,0,0,0.25)"
      : "0 4px 16px rgba(93, 95, 239, 0.12)",
    fontFamily: "Arial, sans-serif",
    transition: "all 0.3s ease",
  },

  header: {
    marginBottom: "12px",
    paddingBottom: "10px",
    borderBottom: `2px solid ${isDarkTheme ? "#5A5698" : "#c9caf0"}`,
  },

  title: {
    fontSize: isMobile ? "14px" : "15px",
    fontWeight: "600",
    margin: 0,
    color: isDarkTheme ? "white" : "#333",
    letterSpacing: "-0.02em",
  },

  detailsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  detailRow: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "4px" : "10px",
    alignItems: isMobile ? "flex-start" : "center",
  },

  label: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    fontSize: "13px",
    fontWeight: "500",
    minWidth: isMobile ? "auto" : "140px",
  },

  value: {
    fontSize: "13px",
    fontWeight: "600",
    color: isDarkTheme ? "white" : "#333",
  },

  codesWrapper: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },

  codeBadge: {
    fontSize: "13px",
    fontWeight: "600",
    color: isDarkTheme ? "white" : "#333",
    fontFamily: "monospace",
    backgroundColor: isDarkTheme ? "#5A5698" : "#c9caf0",
    padding: "4px 10px",
    borderRadius: "6px",
  },

  placeholder: {
    fontSize: "13px",
    fontStyle: "italic",
    color: isDarkTheme ? "#9A99B8" : "#7879c7",
    fontWeight: "400",
  },

  notesSection: {
    marginTop: "4px",
    padding: isMobile ? "10px" : "12px",
    backgroundColor: isDarkTheme ? "#5A5698" : "#c9caf0",
    borderRadius: "10px",
    borderLeft: `4px solid ${isDarkTheme ? "#8D8ED5" : "#7879c7"}`,
    boxShadow: isDarkTheme
      ? "0 2px 8px rgba(0,0,0,0.15)"
      : "0 2px 8px rgba(93, 95, 239, 0.08)",
  },

  notesHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "8px",
  },

  notesIcon: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    flexShrink: 0,
  },

  notesLabel: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },

  notesContent: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  noteItem: (index, totalNotes) => ({
    fontSize: "13px",
    lineHeight: "1.5",
    color: isDarkTheme ? "#E8E8F5" : "#333",
    whiteSpace: "pre-wrap",
    fontWeight: "400",
    paddingBottom: index < totalNotes - 1 ? "10px" : "0",
    borderBottom:
      index < totalNotes - 1
        ? `1px solid ${isDarkTheme ? "#6B6799" : "#b5b7e6"}`
        : "none",
  }),
});

export default ProcedureCard;
